import React, { useState, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import { useParams } from 'react-router-dom'

import axios from '../../dataClient/dataClient'
import { formatPIRDetailsForDisplay, formatPIRSignatures, formatRecipientsForResendDownloadLink } from '../../util/pirFormatting.js'
import { getPIRFileList, getPIRById } from '../../dataClient/apiCalls'
import { isPirPaymentEnabled } from '../../util/pirConsts.js'
import pirTerminology from '../../util/pirTerminology'
import {
  PageWithTitle,
  HeaderWithButtons,
  MedBGContainer,
  FlexRow,
  FlexColumn,
  LightBGContainer,
  FlexEndContainer,
  SpaceBetweenRowContainer
} from '../styles/Containers.js'
import BodyText, { PageTitle } from '../styles/Text'
import Button, { SecondaryButton } from '../common/Button'
import { ErrorMessage } from '../common/UserMessage'
import LoadingError from '../common/LoadingError'
import ResponseEmail from '../pirResponse/ResponseEmail'
import ResponseNote from '../pirResponse/ResponseNote'
import RequestDetails from '../pirResponse/RequestDetails'
import Loading from '../common/Loading'
import RejectButton from '../pirResponse/RejectButton'
import PIRStatus from '../pir/PIRStatus.js'
import ResendDownloadLinkButton from '../pirResponse/ResendDownloadLinkButton.js'
import ResendPirReceiptPopUp from '../pirResponse/ResendPirReceiptPopUp.js'
import ResponseConfirmation from '../common/ResponseConfirmation.js'
import FinalDocuments from '../common/FinalDocuments'
import EditButton from '../pirResponse/EditButton.js'
import { config } from '../../config'

const TitleContainer = styled(FlexRow)`
  align-items: center;
`

const Header = styled(HeaderWithButtons)`
  margin-bottom: 30px;
  margin-top: 30px;
`

const ColumnHeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 15px;
`

const HeaderButtons = styled(ColumnHeaderGroup)`
  gap: 15px;
  flex-direction: row;
  padding-top: 0;
`

const ContentContainer = styled(LightBGContainer)`
  display: flex;
  gap: 50px;
  padding: 50px;

  @media only screen and (max-width: 1000px) {
    gap: 30px;
  }
`

const LeftColumn = styled(FlexColumn)`
  width: 65%;
`

const RequestContainer = styled(MedBGContainer)`
  width: 35%;
  padding: 30px;
  height: max-content;
`

const OWNER_VERIFICATION = config.pir.gis.ownerVerification

function PIRResponsePage() {
  const { pirId } = useParams()

  const [loadingList, setLoadingList] = useState(true)
  const [loadingDetails, setLoadingDetails] = useState(true)
  const [loadingSignatures, setLoadingSignatures] = useState(true)
  const [errorMessage, setErrorMessage] = useState()

  const [fileList, setFileList] = useState([])
  const [pirDetails, setPirDetails] = useState()
  const [pirSignatures, setPirSignatures] = useState([])
  const [ownerDetails, setOwnerDetails] = useState({})

  const [submitLoading, setSubmitLoading] = useState()
  const [submitError, setSubmitError] = useState(false)
  const [reject, setReject] = useState()

  const [editPirLoading, setEditPirLoading] = useState(false)
  const [editPirError, setEditPIRError] = useState(false)

  const [propertyOverlayLoading, setPropertyOverlayLoading] = useState(false)
  const [propertyOverlayError, setPropertyOverlayError] = useState(false)
  const [resendReceiptOpen, setResendReceiptOpen] = useState(false)

  function getFileList(pirId) {
    getPIRFileList(pirId)
      .then(({ data }) => setFileList(data.data))
      .catch(e => {
        console.error('failed to retrieve the file list for PIR', pirId, e)

        if (e.response && e.response.data.message === `pirId not found: ${pirId}`) {
          setErrorMessage([`The provided ${pirTerminology.getRequestName()} does not exist.`])
        } else {
          setErrorMessage([])
        }
      })
      .finally(() => setLoadingList(false))
  }

  function getOwnerDetails({ address, unit, firstName, lastName, companyName }) {
    axios
      .post(
        '/property-owner',
        {
          address,
          unit,
          firstName,
          lastName,
          companyName
        },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .then(({ data }) => {
        setOwnerDetails(data.data)
      })
      .catch(e => {
        setOwnerDetails({ error: true })
        console.error('getOwnerDetails:', { pirId, e })
      })
  }

  function getPIRDetails(pirId) {
    return getPIRById(pirId)
      .then(({ data }) => {
        const pirData = { ...data.data, id: data.data.id.toString() }
        setPirDetails(pirData)
        return pirData
      })
      .catch(e => {
        console.error('Failed to retrieve details for PIR', pirId, e)
        if (e.response?.data.message === `pirId not found: ${pirId}`) {
          setErrorMessage([`The provided ${pirTerminology.getRequestName()} does not exist.`])
        } else {
          setErrorMessage([])
        }

        return null
      })
      .finally(() => setLoadingDetails(false))
  }

  function getSignatures(pirId) {
    axios
      .get(`/pir/${pirId}/signatures`)
      .then(({ data }) => setPirSignatures(formatPIRSignatures(data.data)))
      .catch(e => {
        console.error('failed to retrieve details for PIR', pirId, e)

        if (e.response && e.response.data.message === `pirId not found: ${pirId}`) {
          setErrorMessage([`The provided ${pirTerminology.getRequestName()} does not exist.`])
        } else {
          setErrorMessage([])
        }
      })
      .finally(() => setLoadingSignatures(false))
  }

  useEffect(() => {
    if (pirId) {
      const fetchData = async () => {
        getFileList(pirId)
        getSignatures(pirId)
        const pirDetails = await getPIRDetails(pirId)

        if (pirDetails && OWNER_VERIFICATION) {
          const {
            propertyInformation: { address, unit },
            ownerInformation: { firstName, lastName, companyName }
          } = pirDetails

          getOwnerDetails({ address, unit, firstName, lastName, companyName })
        }
      }

      fetchData()
    }
  }, [pirId])

  const handleUpdateFileList = useCallback(newFileList => {
    setFileList(newFileList)
  }, [])

  const handleUpdateResponseNote = useCallback(newResponseNote => {
    setPirDetails(details => ({
      ...details,
      responseNote: newResponseNote
    }))
  }, [])

  if (errorMessage !== undefined) {
    return <LoadingError message={errorMessage} />
  }

  function handleSubmit() {
    if (fileList.length > 0) {
      setSubmitLoading(true)
      axios
        .post(`/pir/${pirId}/response`)
        .then(() => {
          setSubmitLoading(false)
          setReject(false)
        })
        .catch(e => {
          console.error('failed to send response email', pirId, e)
          setSubmitError('serverError')
          setSubmitLoading(false)
        })
    } else {
      console.error('cannot submit a PIR with an empty response', pirId)
      setSubmitError('emptyError')
    }
  }

  function handleRegeneratePropertyOverlay() {
    setPropertyOverlayError(false)
    setPropertyOverlayLoading(true)

    axios
      .post(`/property-overlay/${pirId}`)
      .then(() => {
        getFileList(pirId)
      })
      .catch(e => {
        console.error('failed to regenerate property overlay', pirId, e)
        setPropertyOverlayError(true)
      })
      .finally(() => setPropertyOverlayLoading(false))
  }

  const handleReject = () => setReject(true)

  function handleSignatureUploadSuccess() {
    getFileList(pirId)
    getSignatures(pirId)
    return getPIRDetails(pirId)
  }

  function handleRequestUpdated() {
    handleSignatureUploadSuccess().then(pirData => {
      if (pirData && OWNER_VERIFICATION) {
        const {
          propertyInformation: { address, unit },
          ownerInformation: { firstName, lastName, companyName }
        } = pirData
        getOwnerDetails({ address, unit, firstName, lastName, companyName })
      }
    })
  }

  function handleEditPir() {
    setEditPirLoading(true)
    setEditPIRError(false)

    axios
      .put(`/pir/${pirId}`, { status: 'READY' })
      .then(({ data }) => {
        setPirDetails(data.data)
      })
      .catch(e => {
        setEditPIRError(true)
        console.error('error updating pir status from COMPLETED to READY:', { pirId, e })
      })
      .finally(() => setEditPirLoading(false))
  }

  if (loadingList || loadingDetails || loadingSignatures) return <Loading />

  if (pirDetails.status === 'NEW') return <LoadingError message={[`This ${pirTerminology.getRequestShortName()} is not ready for review yet.`]} />

  const showButtons = ['PAID', 'SIGNING', 'READY', 'COMPLETED'].includes(pirDetails.status)
  const showReject = ['PAID', 'SIGNING', 'READY'].includes(pirDetails.status)
  const disableEdit = ['PAID', 'SIGNING', 'COMPLETED', 'REJECTED', 'EXPIRED'].includes(pirDetails.status)

  if (reject !== undefined) {
    return <ResponseConfirmation requestType="pir" isRejected={reject} requestId={pirId} secondaryId={pirDetails.secondaryId} fileList={fileList} />
  }

  return (
    <PageWithTitle>
      <Header>
        <TitleContainer>
          <PageTitle>{pirDetails.secondaryId} Response</PageTitle>
          <PIRStatus status={pirDetails.status} />
        </TitleContainer>
        {showButtons && (
          <ColumnHeaderGroup>
            <HeaderButtons>
              {isPirPaymentEnabled() && <SecondaryButton onClick={() => setResendReceiptOpen(true)}>Resend Receipt</SecondaryButton>}
              {showReject && <RejectButton pirId={pirId} onSuccess={handleReject} disabled={submitLoading} />}
              {pirDetails.status === 'READY' && (
                <Button loading={submitLoading} onClick={handleSubmit}>
                  Submit
                </Button>
              )}
              {pirDetails.status === 'COMPLETED' && (
                <>
                  <ResendDownloadLinkButton pirId={pirId} {...formatRecipientsForResendDownloadLink(pirDetails)} />
                  <EditButton onProceed={handleEditPir} loading={editPirLoading} />
                </>
              )}
            </HeaderButtons>
            <ResendPirReceiptPopUp
              pirId={pirId}
              secondaryId={pirDetails.secondaryId}
              email={pirDetails.requestorInformation.email}
              open={resendReceiptOpen}
              onClose={() => setResendReceiptOpen(false)}
            />
            {submitError && submitError === 'serverError' && <ErrorMessage />}
            {submitError && submitError === 'emptyError' && <ErrorMessage>A response must include at least one attachment.</ErrorMessage>}
            {editPirError && (
              <ErrorMessage>There was an error updating the {pirTerminology.getRequestShortName()} to editable. Please try again.</ErrorMessage>
            )}
          </ColumnHeaderGroup>
        )}
      </Header>
      <ContentContainer>
        <LeftColumn>
          {pirDetails && (
            <FlexColumn gap="12px">
              <BodyText>The following email will be sent to the requestor when you submit your response:</BodyText>
              <MedBGContainer padding="30px">
                <ResponseEmail pirDetails={pirDetails} />
              </MedBGContainer>
              <ResponseNote pirId={pirId} responseNote={pirDetails.responseNote} onUpdateNote={handleUpdateResponseNote} />
            </FlexColumn>
          )}
          <FlexColumn gap="6px">
            <SpaceBetweenRowContainer>
              <BodyText>The following files will be attached to your response:</BodyText>
              <SecondaryButton
                loading={propertyOverlayLoading}
                disabled={submitLoading || propertyOverlayLoading || disableEdit}
                onClick={handleRegeneratePropertyOverlay}>
                Regenerate Property Overlay
              </SecondaryButton>
            </SpaceBetweenRowContainer>
            {propertyOverlayError && (
              <FlexEndContainer>
                <ErrorMessage>There was an error regenerating the property overlay. Please try again.</ErrorMessage>
              </FlexEndContainer>
            )}
            <FinalDocuments requestType="pir" requestId={pirId} fileList={fileList} editable={!disableEdit} updateFileList={handleUpdateFileList} />
          </FlexColumn>
        </LeftColumn>
        {pirDetails && (
          <RequestContainer>
            <RequestDetails
              pirDetails={formatPIRDetailsForDisplay(pirDetails)}
              pir={pirDetails}
              ownerDetails={ownerDetails}
              pirSignatures={pirSignatures}
              onSignatureUploadSuccess={handleSignatureUploadSuccess}
              onRequestUpdated={handleRequestUpdated}
            />
          </RequestContainer>
        )}
      </ContentContainer>
    </PageWithTitle>
  )
}

export default PIRResponsePage
